import React from 'react';
import { Drawer, Button, Box, IconButton, Tooltip, Typography, Avatar, Divider, Chip } from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import { useLocation, Link } from 'react-router-dom';
import {
  DashboardOutlined,
  AssessmentOutlined,
  AdminPanelSettingsOutlined,
  FeaturedPlayListOutlined,
  SettingsOutlined,
  ChevronLeft,
  ChevronRight,
  PersonOutlined,
  BarChartOutlined,
} from '@mui/icons-material';
import { useNavbar } from '../context/NavbarContext';
import { useSelector } from "react-redux";

const Navbar = () => {
  const { isNavbarOpen, toggleNavbar } = useNavbar();
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const location = useLocation();
  const currentUser = useSelector((state) => state.auth.user);

  const isActive = (path) => location.pathname === path;

  const buttonStyle = {
    width: '100%',
    justifyContent: isNavbarOpen ? 'flex-start' : 'center',
    paddingLeft: isNavbarOpen ? '20px' : '0px',
    paddingY: '12px',
    color: theme.palette.text.secondary,
    backgroundColor: 'transparent',
    textTransform: 'none',
    borderRadius: '8px',
    transition: 'all 0.3s ease',
    '&:hover': {
      backgroundColor: alpha(theme.palette.primary.main, 0.08),
      color: theme.palette.primary.main,
    },
    '&:before': isNavbarOpen ? {} : {},
    '& .MuiButton-startIcon': {
      marginRight: isNavbarOpen ? '12px' : '0',
      '& .MuiSvgIcon-root': {
        fontSize: '20px',
      }
    }
  };

  const activeButtonStyle = {
    ...buttonStyle,
    backgroundColor: alpha(theme.palette.brand.royalBlue || theme.palette.primary.main, 0.12),
    color: theme.palette.brand.royalBlue || theme.palette.primary.main,
    fontWeight: '600',
    borderLeft: isNavbarOpen ? `4px solid ${theme.palette.brand.royalBlue || theme.palette.primary.main}` : 'none',
  };

  const iconStyle = {
    color: theme.palette.text.secondary,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
  };

  const activeIconStyle = { ...iconStyle, color: theme.palette.primary.main };

  return (
    <Drawer
      sx={{
        bgcolor: 'transparent',
        width: isNavbarOpen ? 240 : 70,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: isNavbarOpen ? 240 : 70,
          boxSizing: 'border-box',
          overflowX: 'hidden',
          overflowY: 'hidden',
          transition: 'width 0.2s ease',
          borderRight: isDark ? '1px solid rgba(255, 255, 255, 0.08)' : '1px solid rgba(0, 0, 0, 0.12)',
          boxShadow: 'none',
          backgroundColor: isDark ? 'rgba(17, 24, 39, 0.96)' : 'rgba(255, 255, 255, 0.96)',
          backdropFilter: 'blur(8px)',
          height: 'calc(100vh - 65px)',
          position: 'fixed',
          top: '65px',
          left: 0,
          right: 'auto',
          borderTopRightRadius: 0,
          borderBottomRightRadius: 0,
          borderTopLeftRadius: 0,
          borderBottomLeftRadius: 0,
          zIndex: (theme) => theme.zIndex.drawer,
        },
      }}
      variant="persistent"
      anchor="left"
      open={true}
    >
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column', 
        paddingTop: '16px', 
        px: isNavbarOpen ? '12px' : '4px',
        gap: '4px',
        borderBottom: '1px solid rgba(33, 150, 243, 0.05)',
        pb: '8px',
        height: '100%',
        overflowY: 'hidden',
        position: 'relative',
      }} role="navigation" aria-label="Primary">
        {/* Header spacing under AppBar */}
        <Box sx={{ height: '8px' }} />

        {/* Toggle Button */}
        <Box sx={{ 
          textAlign: 'center', 
          mb: 1,
          '& .MuiIconButton-root': {
            color: theme.palette.text.secondary,
            '&:hover': {
              backgroundColor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0, 0, 0, 0.04)',
              color: theme.palette.primary.main,
            }
          }
        }}>
          <IconButton onClick={toggleNavbar} aria-label={isNavbarOpen ? 'Collapse sidebar' : 'Expand sidebar'}
            sx={{ '&:focus-visible': { outline: `2px solid ${theme.palette.primary.main}`, outlineOffset: 2, borderRadius: '8px' } }}>
            {isNavbarOpen ? (
              <ChevronLeft sx={{ fontSize: '22px' }} />
            ) : (
              <ChevronRight sx={{ fontSize: '22px' }} />
            )}
          </IconButton>
        </Box>

        {/* Section label: Overview */}
        {isNavbarOpen && (
          <Typography sx={{ color: theme.palette.text.secondary, opacity: 0.7, fontSize: '12px', px: '16px', py: '8px' }}>
            Overview
          </Typography>
        )}

        {/* Menu Items */}
        {[
          { path: '/dashboard', label: 'Dashboard', icon: <DashboardOutlined /> },
          { path: '/individual-sites', label: 'Sites', icon: <FeaturedPlayListOutlined /> },
          ...(currentUser
            ? [{ path: '/reports-analytics', label: 'Reports & Analytics', icon: <BarChartOutlined /> }]
            : []),
          // Hidden for now:
          // { path: '/operations', label: 'Operations', icon: <WorkOutlineOutlined /> },
          // { path: '/maintenance', label: 'Maintenance', icon: <EngineeringOutlined /> },
          // { path: '/energy-management', label: 'Energy Management', icon: <OfflineBoltOutlined /> },
          // { path: '/alert-management', label: 'Alert Management', icon: <AddAlertOutlined /> },
          // { path: '/administration', label: 'Administration', icon: <AdminPanelSettingsOutlined /> },
          // { path: '/authentication', label: 'Authentication', icon: <GppGoodOutlined /> },
        ].map(({ path, label, icon }) => (
          <Link to={path} style={{ textDecoration: 'none' }} key={path} aria-label={label} aria-current={isActive(path) ? 'page' : undefined}>
            <Tooltip title={!isNavbarOpen ? label : ''} placement="right">
              <Button
                sx={{
                  ...(isActive(path) ? {
                    ...activeButtonStyle,
                    border: `1px solid ${alpha(theme.palette.brand.royalBlue || theme.palette.primary.main, 0.24)}`,
                    ...(isNavbarOpen ? {} : { width: 'calc(100% - 12px)', mx: 'auto' })
                  } : {
                    ...buttonStyle,
                  }),
                  minHeight: '40px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: isNavbarOpen ? 'flex-start' : 'center',
                  padding: isNavbarOpen ? '0 20px' : '4px 0',
                  borderRadius: '10px',
                  '&:focus-visible': { outline: `2px solid ${theme.palette.brand.royalBlue || '#0A84FF'}`, outlineOffset: 2, borderRadius: '10px' },
                  '& .MuiButton-startIcon': {
                    marginRight: isNavbarOpen ? '12px' : '0',
                    '& .MuiSvgIcon-root': {
                      fontSize: '20px',
                    }
                  }
                }}
                startIcon={isNavbarOpen && icon}
              >
                {isNavbarOpen ? (
                  <Typography
                    sx={{
                      fontSize: '0.875rem',
                      fontWeight: isActive(path) ? 500 : 400,
                    }}
                  >
                    {label}
                  </Typography>
                ) : (
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    width: '100%',
                    height: '100%'
                  }}>
                    {React.cloneElement(icon, {
                      sx: {
                        ...(isActive(path) ? activeIconStyle : iconStyle),
                        fontSize: '20px',
                      }
                    })}
                  </Box>
                )}
              </Button>
            </Tooltip>
          </Link>
        ))}

        {/* Section label: Admin */}
        {isNavbarOpen && currentUser && (currentUser.role === 'Admin' || currentUser.role === 'Manager') && (
          <Typography sx={{ color: theme.palette.text.secondary, opacity: 0.7, fontSize: '12px', px: '16px', py: '8px', mt: 1 }}>
            Admin
          </Typography>
        )}

        {(currentUser && (currentUser.role === 'Admin' || currentUser.role === 'Manager')) && [
          { path: '/user-access', label: 'User Access', icon: <AdminPanelSettingsOutlined /> },
          { path: '/user-permissions', label: 'User Permissions', icon: <SettingsOutlined /> },
          { path: '/admin-logs', label: 'System Logs', icon: <AssessmentOutlined /> },
        ].map(({ path, label, icon }) => (
          <Link to={path} style={{ textDecoration: 'none' }} key={path} aria-label={label} aria-current={isActive(path) ? 'page' : undefined}>
            <Tooltip title={!isNavbarOpen ? label : ''} placement="right">
              <Button
                sx={{
                  ...(isActive(path) ? {
                    ...activeButtonStyle,
                    border: `1px solid ${alpha(theme.palette.brand.royalBlue || theme.palette.primary.main, 0.24)}`,
                    ...(isNavbarOpen ? {} : { width: 'calc(100% - 12px)', mx: 'auto' })
                  } : {
                    ...buttonStyle,
                  }),
                  minHeight: '40px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: isNavbarOpen ? 'flex-start' : 'center',
                  padding: isNavbarOpen ? '0 20px' : '4px 0',
                  borderRadius: '10px',
                  '&:focus-visible': { outline: `2px solid ${theme.palette.brand.royalBlue || '#0A84FF'}`, outlineOffset: 2, borderRadius: '10px' },
                  '& .MuiButton-startIcon': {
                    marginRight: isNavbarOpen ? '12px' : '0',
                    '& .MuiSvgIcon-root': {
                      fontSize: '20px',
                    }
                  }
                }}
                startIcon={isNavbarOpen && icon}
              >
                {isNavbarOpen ? (
                  <Typography
                    sx={{
                      fontSize: '0.875rem',
                      fontWeight: isActive(path) ? 500 : 400,
                    }}
                  >
                    {label}
                  </Typography>
                ) : (
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    width: '100%',
                    height: '100%'
                  }}>
                    {React.cloneElement(icon, {
                      sx: {
                        ...(isActive(path) ? activeIconStyle : iconStyle),
                        fontSize: '20px',
                      }
                    })}
                  </Box>
                )}
              </Button>
            </Tooltip>
          </Link>
        ))}

        {/* Empty Box to Push Profile to Bottom */}
        <Box sx={{ flexGrow: 1 }} />

        {/* User Profile Section */}
        {currentUser && (
          <>
            <Divider sx={{ 
              my: 1, 
              borderColor: isDark ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.12)',
              mx: isNavbarOpen ? 0 : 1,
            }} />
            <Link to="/profile" style={{ textDecoration: 'none' }} aria-label="View Profile">
              <Tooltip title={!isNavbarOpen ? 'View Profile' : ''} placement="right">
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: isNavbarOpen ? 1 : 0,
                    padding: isNavbarOpen ? '12px' : '8px',
                    borderRadius: '12px',
                    backgroundColor: isActive('/profile') 
                      ? alpha(theme.palette.brand.royalBlue || theme.palette.primary.main, 0.12)
                      : 'transparent',
                    border: isActive('/profile') 
                      ? `1px solid ${alpha(theme.palette.brand.royalBlue || theme.palette.primary.main, 0.24)}`
                      : 'none',
                    transition: 'all 0.3s ease',
                    cursor: 'pointer',
                    '&:hover': {
                      backgroundColor: alpha(theme.palette.brand.royalBlue || theme.palette.primary.main, 0.08),
                    },
                    '&:focus-visible': { 
                      outline: `2px solid ${theme.palette.brand.royalBlue || '#0A84FF'}`, 
                      outlineOffset: 2, 
                      borderRadius: '12px' 
                    },
                    justifyContent: isNavbarOpen ? 'flex-start' : 'center',
                  }}
                >
                  <Avatar
                    sx={{
                      width: isNavbarOpen ? 36 : 32,
                      height: isNavbarOpen ? 36 : 32,
                      bgcolor: theme.palette.brand.royalBlue || theme.palette.primary.main,
                      color: '#fff',
                      fontSize: isNavbarOpen ? '0.875rem' : '0.75rem',
                      fontWeight: 600,
                    }}
                  >
                    {currentUser.email?.charAt(0).toUpperCase() || <PersonOutlined />}
                  </Avatar>
                  {isNavbarOpen && (
                    <Box sx={{ 
                      flex: 1, 
                      minWidth: 0,
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 0.5,
                    }}>
                      <Typography
                        sx={{
                          fontSize: '0.875rem',
                          fontWeight: 600,
                          color: theme.palette.text.primary,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {currentUser.fullName || currentUser.name || currentUser.email?.split('@')[0] || 'User'}
                      </Typography>
                      <Chip
                        label={currentUser.role || 'User'}
                        size="small"
                        sx={{
                          height: '20px',
                          fontSize: '0.7rem',
                          fontWeight: 500,
                          backgroundColor: (() => {
                            const role = currentUser.role || 'User';
                            if (role === 'Admin') {
                              return isDark ? 'rgba(230,185,5,0.18)' : 'rgba(230,185,5,0.10)';
                            } else if (role === 'Manager') {
                              return isDark ? 'rgba(91,141,239,0.18)' : 'rgba(91,141,239,0.10)';
                            } else if (role === 'Engineer') {
                              return isDark ? 'rgba(20,184,166,0.18)' : 'rgba(20,184,166,0.10)';
                            }
                            return isDark ? 'rgba(100,116,139,0.18)' : 'rgba(100,116,139,0.10)';
                          })(),
                          color: (() => {
                            const role = currentUser.role || 'User';
                            if (role === 'Admin') {
                              return theme.palette.brand?.gold || '#E6B905';
                            } else if (role === 'Manager') {
                              return theme.palette.brand?.iris || '#5B8DEF';
                            } else if (role === 'Engineer') {
                              return theme.palette.accents?.teal || '#14B8A6';
                            }
                            return theme.palette.accents?.slate || '#64748B';
                          })(),
                          border: (() => {
                            const role = currentUser.role || 'User';
                            if (role === 'Admin') {
                              return isDark ? '1px solid rgba(230,185,5,0.32)' : '1px solid rgba(230,185,5,0.20)';
                            } else if (role === 'Manager') {
                              return isDark ? '1px solid rgba(91,141,239,0.30)' : '1px solid rgba(91,141,239,0.20)';
                            } else if (role === 'Engineer') {
                              return isDark ? '1px solid rgba(20,184,166,0.30)' : '1px solid rgba(20,184,166,0.20)';
                            }
                            return isDark ? '1px solid rgba(100,116,139,0.30)' : '1px solid rgba(100,116,139,0.20)';
                          })(),
                          '& .MuiChip-label': {
                            px: 0.75,
                            fontSize: '0.7rem',
                          },
                        }}
                      />
                    </Box>
                  )}
                </Box>
              </Tooltip>
            </Link>
          </>
        )}
      </Box>
    </Drawer>
  );
};

export default Navbar;
